// Complete export.ts file with tainted canvas fixes

// Types
export type ExportBackground = 'transparent' | 'white' | 'black';

export interface ExportOptions {
  background?: ExportBackground;
  filename?: string;
  scale?: number;
  quality?: number;
}

export interface ViewportMetadata {
  scale: number;
  domain: { start: number; end: number };
  numberType: string;
  timestamp: string;
}

/**
 * Clean SVG content to prevent tainted canvas errors
 */
function cleanSVGForExport(svgElement: SVGElement): SVGElement {
  const clonedSVG = svgElement.cloneNode(true) as SVGElement;
  
  // Remove or replace external font references
  const styleElements = clonedSVG.querySelectorAll('style');
  styleElements.forEach(style => {
    if (style.textContent) {
      // Replace external font URLs with safe alternatives
      style.textContent = style.textContent
        .replace(/@import url\([^)]+\);?/g, '') // Remove @import statements
        .replace(/url\([^)]+\)/g, 'none') // Remove external URLs
        .replace(/font-family:\s*["']?[^"';]+["']?/g, 'font-family: Arial, sans-serif'); // Use safe fonts
    }
  });
  
  // Remove external image references
  const imageElements = clonedSVG.querySelectorAll('image');
  imageElements.forEach(img => {
    const href = img.getAttribute('href') || img.getAttribute('xlink:href');
    if (href && (href.startsWith('http') || href.startsWith('//'))) {
      img.remove(); // Remove external images
    }
  });
  
  // Clean up foreignObject elements that might contain external content
  const foreignObjects = clonedSVG.querySelectorAll('foreignObject');
  foreignObjects.forEach(obj => {
    const htmlContent = obj.innerHTML;
    // Remove any external links or resources
    obj.innerHTML = htmlContent
      .replace(/src="[^"]*"/g, '')
      .replace(/href="[^"]*"/g, '')
      .replace(/@import[^;]+;/g, '');
  });
  
  // Ensure all text uses system fonts
  const textElements = clonedSVG.querySelectorAll('text');
  textElements.forEach(text => {
    text.style.fontFamily = 'Arial, sans-serif';
  });
  
  return clonedSVG;
}

/**
 * Apply high contrast colors for black background
 */
function applyHighContrastColors(svgElement: SVGElement): void {
  // Change dark colors to white for visibility on black background
  const elements = svgElement.querySelectorAll('*');
  elements.forEach(element => {
    if (element instanceof SVGElement) {
      const stroke = element.getAttribute('stroke');
      const fill = element.getAttribute('fill');
      
      if (stroke && isLikelyDarkColor(stroke)) {
        element.setAttribute('stroke', '#ffffff');
      }
      if (fill && isLikelyDarkColor(fill)) {
        element.setAttribute('fill', '#ffffff');
      }
    }
  });
  
  // Handle text elements specifically
  const textElements = svgElement.querySelectorAll('text');
  textElements.forEach(text => {
    text.style.fill = '#ffffff';
    text.style.color = '#ffffff';
  });
  
  // Handle foreignObject content
  const foreignObjects = svgElement.querySelectorAll('foreignObject');
  foreignObjects.forEach(foreignObj => {
    const htmlContent = foreignObj.innerHTML;
    if (htmlContent) {
      // Replace dark colors with white
      foreignObj.innerHTML = htmlContent.replace(/(color:\s*(?:#000000|#000|black|#333|#666|#999|#DC2626|#EF4444|red)[^;]+)/gi, 'color: #ffffff');
    }
  });
}

/**
 * Capture the KaTeX stylesheet from the live document so we can inline it into
 * cloned documents/SVGs used for export. Without this, `foreignObject` labels
 * lose their fraction bar styling when html2canvas or serialized SVG is drawn
 * to a canvas.
 */
function extractKaTeXCSS(): string | null {
  try {
    // 1. Try finding specific KaTeX style tag
    const inlineStyle = Array.from(document.querySelectorAll('style')).find(
      (style) => style.textContent && style.textContent.includes('.katex')
    );
    if (inlineStyle?.textContent) {
      return inlineStyle.textContent;
    }

    // 2. Try finding linked stylesheet with 'katex' in URL
    const katexLink = Array.from(
      document.querySelectorAll('link[rel="stylesheet"]') as NodeListOf<HTMLLinkElement>
    ).find((link) => link.href && link.href.includes('katex'));

    if (katexLink?.sheet) {
      const sheet = katexLink.sheet as CSSStyleSheet;
      try {
        const cssText = Array.from(sheet.cssRules || [])
          .map((rule) => rule.cssText)
          .join('\n');
        if (cssText.includes('.katex')) return cssText;
      } catch {
        // Accessing cssRules can throw for cross-origin styles; ignore.
      }
    }

    // 3. Robust fallback: Scan ALL stylesheets for KaTeX rules
    // This handles bundled CSS where 'katex' isn't in the filename
    for (const sheet of Array.from(document.styleSheets)) {
      try {
        // Skip if we can't access rules (CORS)
        const rules = sheet.cssRules || sheet.rules;
        if (!rules) continue;

        // Check if this sheet contains KaTeX definitions
        // We check for a characteristic KaTeX class in the first few rules for performance
        let hasKatex = false;
        // Check a sampling of rules to see if this looks like a vendor/katex bundle
        // or iterate all if we want to be sure (might be slow but safer for export)
        for (let i = 0; i < rules.length; i++) { 
           if (rules[i].cssText && rules[i].cssText.includes('.katex')) {
             hasKatex = true;
             break;
           }
        }

        if (hasKatex) {
           // Found it! Extract the whole sheet
           const cssText = Array.from(rules)
             .map((rule) => rule.cssText)
             .join('\n');
           return cssText;
        }
      } catch (e) {
        // Continue to next sheet if access denied
      }
    }

  } catch {
    // No-op
  }
  return null;
}

function injectKaTeXStyles(target: Document | SVGElement): void {
  const katexCss = extractKaTeXCSS();
  if (!katexCss) return;

  if (target instanceof Document) {
    // Only handle HTML documents; skip XML/SVG documents
    if ((target as Document).nodeType !== 9) return;
    if ((target as Document).contentType && (target as Document).contentType !== 'text/html') return;

    // Avoid duplicate injection
    if (target.querySelector('style[data-export-inline-katex="true"]')) return;

    // Require a head/body/html host; if absent, skip to avoid HierarchyRequestError
    const head = target.head || target.getElementsByTagName('head')[0];
    const html = target.documentElement;
    const body = target.body || target.getElementsByTagName('body')[0];
    if (!head && !html && !body) return;

    const styleEl = target.createElement('style');
    styleEl.setAttribute('data-export-inline-katex', 'true');
    styleEl.textContent = katexCss;

    try {
      if (head) {
        head.appendChild(styleEl);
        return;
      }
      if (html) {
        html.insertBefore(styleEl, html.firstChild);
        return;
      }
      if (body) {
        body.appendChild(styleEl);
      }
    } catch (e) {
      // Swallow to avoid breaking export; missing host is acceptable
      return;
    }
    return;
  }

  // SVGElement path (strict guard)
  if (!(target instanceof SVGElement)) return;

  const styleEl = document.createElementNS('http://www.w3.org/2000/svg', 'style');
  styleEl.setAttribute('data-export-inline-katex', 'true');
  styleEl.textContent = katexCss;
  try {
    const firstElement = target.firstElementChild;
    if (firstElement) {
      target.insertBefore(styleEl, firstElement);
    } else {
      target.appendChild(styleEl);
    }
  } catch {
    // Fallback: ignore if cannot insert
    return;
  }
}

/**
 * Helper function to detect if a color is likely dark (should be changed to white)
 */
function isLikelyDarkColor(color: string): boolean {
  // Handle hex colors
  if (color.startsWith('#')) {
    const hex = color.substring(1);
    if (hex.length === 3) {
      const r = parseInt(hex[0] + hex[0], 16);
      const g = parseInt(hex[1] + hex[1], 16);
      const b = parseInt(hex[2] + hex[2], 16);
      return (r + g + b) < 300;
    } else if (hex.length === 6) {
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return (r + g + b) < 300;
    }
  }
  
  // Handle rgb() colors
  const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (rgbMatch) {
    const r = parseInt(rgbMatch[1]);
    const g = parseInt(rgbMatch[2]);
    const b = parseInt(rgbMatch[3]);
    return (r + g + b) < 300;
  }
  
  // Handle named colors that are likely dark
  const darkColors = ['black', 'darkgray', 'darkgrey', 'gray', 'grey'];
  return darkColors.includes(color.toLowerCase());
}

/**
 * Safe annotation overlay that prevents tainted canvas
 */
async function overlayAnnotationCanvasSafe(
  mainCanvas: HTMLCanvasElement,
  mainCtx: CanvasRenderingContext2D,
  scale: number
): Promise<void> {
  try {
    const annotationSVG = document.querySelector('.annotation-svg') as SVGElement;
    if (!annotationSVG) {
      console.log('No annotation SVG found, skipping overlay');
      return;
    }
    
    console.log('Found annotation SVG, overlaying safely...');
    
    // Clean the annotation SVG
    const cleanedAnnotationSVG = cleanSVGForExport(annotationSVG);
    
    // Create a temporary canvas for the annotation
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    if (!tempCtx) return;
    
    tempCanvas.width = annotationSVG.clientWidth * scale;
    tempCanvas.height = annotationSVG.clientHeight * scale;
    
    // Convert annotation SVG to data URL
    const annotationSvgData = new XMLSerializer().serializeToString(cleanedAnnotationSVG);
    const annotationSvgBlob = new Blob([annotationSvgData], { 
      type: 'image/svg+xml;charset=utf-8' 
    });
    const annotationUrl = URL.createObjectURL(annotationSvgBlob);
    
    // Create image from annotation SVG
    const annotationImg = new Image();
    annotationImg.crossOrigin = 'anonymous'; // Prevent tainted canvas
    
    return new Promise((resolve) => {
      annotationImg.onload = () => {
        tempCtx.drawImage(annotationImg, 0, 0, tempCanvas.width, tempCanvas.height);
        
        // Overlay onto main canvas
        mainCtx.drawImage(tempCanvas, 0, 0);
        
        URL.revokeObjectURL(annotationUrl);
        console.log('Annotation overlay completed safely');
        resolve();
      };
      
      annotationImg.onerror = () => {
        console.warn('Failed to load annotation SVG safely');
        URL.revokeObjectURL(annotationUrl);
        resolve(); // Continue even if annotation fails
      };
      
      annotationImg.src = annotationUrl;
    });
  } catch (error) {
    console.warn('Safe annotation overlay failed:', error);
    // Don't throw - continue with main export
  }
}

/**
 * Enhanced SVG-to-canvas export with tainted canvas prevention
 */
async function exportSVGToCanvasSafe(
  svgElement: SVGElement,
  options: ExportOptions,
  metadata?: ViewportMetadata
): Promise<void> {
  try {
    console.log('Using safe SVG-to-canvas export method...');
    const filename = options.filename || 'number-line-export.png';
    
    // Create a canvas element
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not get canvas context');
    }
    
    // Set canvas dimensions
    const scale = options.scale || 2;
    const svgWidth = svgElement.clientWidth || 400;
    const svgHeight = svgElement.clientHeight || 200;
    
    canvas.width = svgWidth * scale;
    canvas.height = svgHeight * scale;
    
    console.log('Canvas dimensions:', { width: canvas.width, height: canvas.height, scale });
    
    // Set background color
    const backgroundColor = getBackgroundColor(options.background);
    if (backgroundColor) {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    
    // CRITICAL FIX: Clean SVG data to prevent tainted canvas
    const svgClone = svgElement.cloneNode(true) as SVGElement;
    
    // Inline KaTeX styles so fraction labels keep their bars in exports
    injectKaTeXStyles(svgClone);
    
    // Remove any potentially problematic elements
    svgClone.querySelectorAll('image, foreignObject').forEach(el => el.remove());
    
    // Ensure no external references
    svgClone.querySelectorAll('*').forEach(el => {
      // Remove any external href references
      if (el.hasAttribute('href') && el.getAttribute('href')?.startsWith('http')) {
        el.removeAttribute('href');
      }
      if (el.hasAttribute('xlink:href') && el.getAttribute('xlink:href')?.startsWith('http')) {
        el.removeAttribute('xlink:href');
      }
    });
    
    const svgData = new XMLSerializer().serializeToString(svgClone);
    console.log('Cleaned SVG data length:', svgData.length);
    
    // Create data URL directly instead of blob to avoid CORS issues
    const svgDataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgData);
    
    // Create an image from the SVG
    const img = new Image();
    // DON'T set crossOrigin for data URLs
    
    return new Promise((resolve, reject) => {
      img.onload = async () => {
        try {
          // Draw the image to canvas
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          console.log('SVG drawn to canvas successfully');
          
          // Try to overlay annotation canvas if it exists
          try {
            await overlayAnnotationCanvasSafe(canvas, ctx, scale);
          } catch (annotationError) {
            console.warn('Annotation overlay failed, continuing without it:', annotationError);
          }
          
          // Add metadata if provided
          if (metadata) {
            try {
              await embedExportMetadata(canvas, metadata);
            } catch (metadataError) {
              console.warn('Metadata embedding failed, continuing without it:', metadataError);
            }
          }
          
          // FIXED DOWNLOAD: Use a more reliable download method
          console.log('Starting SVG export download...');
          try {
            await downloadCanvasReliably(canvas, filename);
            console.log('SVG export download completed successfully');
          } catch (downloadError) {
            console.error('SVG download failed:', downloadError);
            throw downloadError;
          }
          
          console.log('Safe SVG-to-canvas export completed successfully');
          resolve();
        } catch (error) {
          reject(error);
        }
      };
      
      img.onerror = (error) => {
        console.error('Failed to load SVG as image:', error);
        reject(new Error('Failed to load SVG as image'));
      };
      
      img.src = svgDataUrl;
    });
  } catch (error) {
    console.error('Safe SVG-to-canvas export failed:', error);
    throw error;
  }
}

/**
 * Enhanced html2canvas fallback that captures all elements including annotations
 */
async function exportWithHtml2CanvasEnhanced(
  containerElement: HTMLElement,
  options: ExportOptions,
  metadata?: ViewportMetadata
): Promise<void> {
  try {
    console.log('Using enhanced html2canvas fallback method...');
    const filename = options.filename || 'number-line-export.png';
    
    // Import html2canvas dynamically
    const html2canvas = (await import('html2canvas')).default;
    
    // Get the exact container bounds
    const containerRect = containerElement.getBoundingClientRect();
    console.log('Container bounds:', {
      left: containerRect.left,
      top: containerRect.top,
      width: containerRect.width,
      height: containerRect.height
    });
    
    // CRITICAL: Wait for any pending renders before capture
    await new Promise(resolve => setTimeout(resolve, 500));
    console.log('Waited for render completion');
    
    // DEBUG: Analyze what elements we're trying to capture
    console.log('=== ELEMENT ANALYSIS ===');
    const allElements = containerElement.querySelectorAll('*');
    const visibleElements = Array.from(allElements).filter(el => {
      if (el instanceof HTMLElement) {
        const style = window.getComputedStyle(el);
        const rect = el.getBoundingClientRect();
        return style.display !== 'none' && 
               style.visibility !== 'hidden' && 
               style.opacity !== '0' &&
               rect.width > 0 && 
               rect.height > 0;
      }
      return true;
    });
    
    console.log('Total elements:', allElements.length);
    console.log('Visible elements:', visibleElements.length);
    console.log('Element breakdown:', {
      svgs: containerElement.querySelectorAll('svg').length,
      annotations: containerElement.querySelectorAll('.annotation-canvas, [class*="annotation"]').length,
      secondaryLines: containerElement.querySelectorAll('[class*="secondary"]').length,
      constants: containerElement.querySelectorAll('[class*="constant"]').length,
      jumps: containerElement.querySelectorAll('[class*="jump"]').length
    });
    const canvasOptions = {
      backgroundColor: getBackgroundColor(options.background),
      scale: options.scale || 2,
      useCORS: true,
      allowTaint: true,
      scrollX: 0,
      scrollY: 0,
      width: containerElement.offsetWidth,
      height: containerElement.offsetHeight,
      foreignObjectRendering: true,
      removeContainer: false,
      logging: false,
      
      ignoreElements: (element: Element) => {
        const tagName = element.tagName?.toLowerCase();
        
        // More aggressive filtering for complex content
        if (tagName === 'script' || tagName === 'style') return true;
        
        if (element instanceof HTMLElement) {
          const style = window.getComputedStyle(element);
          
          // Ignore hidden elements
          if (style.display === 'none' || 
              style.visibility === 'hidden' || 
              style.opacity === '0') {
            return true;
          }
          
          // CRITICAL: Ignore elements that might cause conflicts
          const className = element.className || '';
          
          // Skip problematic annotation elements that might interfere
          if (className.includes('pointer-events-none') && 
              className.includes('absolute')) {
            console.log('Ignoring potentially problematic element:', element.tagName, className);
            return true;
          }
        }
        
        return false;
      },
      
      onclone: (clonedDoc: Document) => {
        console.log('=== CLONED DOCUMENT FIXES ===');
        
        // Ensure KaTeX styles are present for fraction labels
        injectKaTeXStyles(clonedDoc);
        
        // Stabilize equivalent fractions layout: disable sticky headers and allow full height
        const eqContainers = clonedDoc.querySelectorAll('[data-equivalent-fractions-container]');
        eqContainers.forEach((container) => {
          const el = container as HTMLElement;
          el.style.overflow = 'visible';
          el.style.maxHeight = 'none';
          el.style.height = 'auto';
          el.style.position = 'relative';
          el.style.paddingTop = el.style.paddingTop || '0px';
          
          // Make sticky toolbars static to avoid overlap
          const stickies = el.querySelectorAll('.sticky');
          stickies.forEach((sticky) => {
            const stickyEl = sticky as HTMLElement;
            stickyEl.style.position = 'relative';
            stickyEl.style.top = '0';
            stickyEl.style.left = '0';
            stickyEl.style.right = '0';
            stickyEl.style.zIndex = '5';
            stickyEl.style.boxShadow = 'none';
          });
          
          const rails = el.querySelectorAll('[data-equivalent-fractions-rail]');
          rails.forEach((rail) => {
            const railEl = rail as HTMLElement;
            railEl.style.overflow = 'visible';
            railEl.style.maxHeight = 'none';
            railEl.style.height = 'auto';
          });
        });
        
        // CRITICAL: Ensure all elements are properly visible in clone
        const clonedContainer = clonedDoc.querySelector('.number-line-container') as HTMLElement;
        if (clonedContainer) {
          clonedContainer.style.position = 'relative';
          clonedContainer.style.left = '0';
          clonedContainer.style.top = '0';
          clonedContainer.style.margin = '0';
          clonedContainer.style.padding = '0';
          clonedContainer.style.overflow = 'visible';
          console.log('Fixed cloned container');
        }
        
        // Fix annotation layers specifically
        const annotationElements = clonedDoc.querySelectorAll('.annotation-canvas, [class*="annotation"]');
        annotationElements.forEach((el, index) => {
          if (el instanceof HTMLElement) {
            el.style.position = 'absolute';
            el.style.top = '0';
            el.style.left = '0';
            el.style.pointerEvents = 'none';
            el.style.zIndex = '1000';
            console.log(`Fixed annotation element ${index + 1}`);
          }
        });
        
        // Ensure SVG elements are visible
        const svgElements = clonedDoc.querySelectorAll('svg');
        svgElements.forEach((svg, index) => {
          svg.style.display = 'block';
          svg.style.visibility = 'visible';
          svg.style.opacity = '1';
          console.log(`Fixed SVG element ${index + 1}`);
        });
        
        // Fix secondary line elements
        const secondaryElements = clonedDoc.querySelectorAll('[class*="secondary"]');
        secondaryElements.forEach((el, index) => {
          if (el instanceof HTMLElement) {
            el.style.visibility = 'visible';
            el.style.opacity = '1';
            console.log(`Fixed secondary element ${index + 1}`);
          }
        });
        
        // Fix constant elements
        const constantElements = clonedDoc.querySelectorAll('[class*="constant"]');
        constantElements.forEach((el, index) => {
          if (el instanceof HTMLElement) {
            el.style.visibility = 'visible';
            el.style.opacity = '1';
            console.log(`Fixed constant element ${index + 1}`);
          }
        });
        
        console.log('=== CLONED DOCUMENT FIXES COMPLETE ===');
      }
    };
    
    console.log('About to capture with enhanced html2canvas...');
    
    const canvas = await html2canvas(containerElement, canvasOptions);
    console.log('Enhanced html2canvas capture completed:', {
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      expectedWidth: containerElement.offsetWidth * (options.scale || 2),
      expectedHeight: containerElement.offsetHeight * (options.scale || 2)
    });
    
    // ENHANCED CONTENT DETECTION with detailed numbers
    const ctx = canvas.getContext('2d');
    if (ctx) {
      const sampleWidth = Math.min(canvas.width, 300);
      const sampleHeight = Math.min(canvas.height, 300);
      const imageData = ctx.getImageData(0, 0, sampleWidth, sampleHeight);
      
      let contentPixels = 0;
      let whitePixels = 0;
      let blackPixels = 0;
      let colorPixels = 0;
      let totalPixels = 0;
      
      for (let i = 0; i < imageData.data.length; i += 4) {
        const r = imageData.data[i];
        const g = imageData.data[i + 1];
        const b = imageData.data[i + 2];
        const alpha = imageData.data[i + 3];
        
        totalPixels++;
        
        if (alpha > 0) {
          contentPixels++;
          
          // Check if it's white
          if (r > 240 && g > 240 && b > 240) {
            whitePixels++;
          }
          // Check if it's black/dark
          else if (r < 50 && g < 50 && b < 50) {
            blackPixels++;
          }
          // It's a color
          else {
            colorPixels++;
          }
        }
      }
      
      const contentPercentage = (contentPixels / totalPixels) * 100;
      const whitePercentage = (whitePixels / totalPixels) * 100;
      const blackPercentage = (blackPixels / totalPixels) * 100;
      const colorPercentage = (colorPixels / totalPixels) * 100;
      
      console.log('=== DETAILED CONTENT ANALYSIS ===');
      console.log('Content detection:', {
        totalPixels,
        contentPixels,
        whitePixels,
        blackPixels,
        colorPixels,
        contentPercentage: contentPercentage.toFixed(2) + '%',
        whitePercentage: whitePercentage.toFixed(2) + '%',
        blackPercentage: blackPercentage.toFixed(2) + '%',
        colorPercentage: colorPercentage.toFixed(2) + '%',
        hasContent: contentPixels > 0,
        mostlyWhite: whitePercentage > 90,
        hasSignificantContent: (blackPixels + colorPixels) > (totalPixels * 0.01) // More than 1% non-white content
      });
      
      // Create preview with more info
      try {
        const previewDataUrl = canvas.toDataURL('image/png');
        console.log('Canvas preview (first 100 chars):', previewDataUrl.substring(0, 100));
        
        // Remove any existing previews first
        const existingPreviews = document.querySelectorAll('img[title*="Export"]');
        existingPreviews.forEach(img => img.remove());
        
        const preview = document.createElement('img');
        preview.src = previewDataUrl;
        preview.style.position = 'fixed';
        preview.style.top = '10px';
        preview.style.right = '10px';
        preview.style.width = '300px';
        preview.style.height = 'auto';
        preview.style.border = whitePercentage > 90 ? '3px solid red' : '3px solid green';
        preview.style.zIndex = '10000';
        preview.style.backgroundColor = 'white';
        preview.title = `Export Preview - ${contentPercentage.toFixed(1)}% content, ${whitePercentage.toFixed(1)}% white - Remove with: document.querySelector("img[title*=Export]").remove()`;
        document.body.appendChild(preview);
        
        console.log('🖼️ CANVAS PREVIEW ADDED TO PAGE');
        console.log(`Border color: ${whitePercentage > 90 ? 'RED (mostly white)' : 'GREEN (has content)'}`);
        console.log('Remove it with: document.querySelector("img[title*=Export]").remove()');
        
      } catch (previewError) {
        console.error('Failed to create canvas preview:', previewError);
      }
      
      // Warn if content seems problematic
      if (whitePercentage > 90) {
        console.warn('⚠️ Canvas is mostly white - content may not be rendering properly');
      }
      if ((blackPixels + colorPixels) < (totalPixels * 0.01)) {
        console.warn('⚠️ Very little non-white content detected - capture may have failed');
      }
    }
    
    // Add metadata if provided
    if (metadata) {
      try {
        await embedExportMetadata(canvas, metadata);
      } catch (metadataError) {
        console.warn('Metadata embedding failed, continuing without it:', metadataError);
      }
    }
    
    // FIXED DOWNLOAD: Use the reliable download method
    console.log('Starting html2canvas export download...');
    try {
      await downloadCanvasReliably(canvas, filename);
      console.log('Enhanced html2canvas export completed successfully');
    } catch (downloadError) {
      console.error('Html2canvas download failed:', downloadError);
      throw downloadError;
    }
    
  } catch (error) {
    console.error('Enhanced html2canvas export failed:', error);
    throw error;
  }
}

// Helper function to fix the main metadata embedding function name
// (I noticed it might be called differently in your code)
async function embedExportMetadata(canvas: HTMLCanvasElement, metadata: ViewportMetadata): Promise<void> {
  try {
    const metadataComment = `Number Line Export - Scale: ${metadata.scale}, Domain: ${metadata.domain.start} to ${metadata.domain.end}, Type: ${metadata.numberType}, Exported: ${metadata.timestamp}`;
    
    // Store in canvas data attribute for potential future use
    canvas.setAttribute('data-export-metadata', JSON.stringify(metadata));
    
    console.log('Metadata embedded:', metadataComment);
  } catch (error) {
    console.warn('Metadata embedding failed:', error);
    // Don't throw - metadata is not critical
  }
}

// NEW: More reliable download function
async function downloadCanvasReliably(canvas: HTMLCanvasElement, filename: string): Promise<void> {
  return new Promise((resolve, reject) => {
    console.log('Creating blob from canvas...');
    
    try {
      canvas.toBlob((blob) => {
        if (!blob) {
          console.error('Failed to create blob from canvas');
          reject(new Error('Failed to create blob from canvas'));
          return;
        }
        
        console.log('Blob created successfully:', {
          size: blob.size,
          type: blob.type
        });
        
        if (blob.size === 0) {
          console.error('Blob is empty (0 bytes)');
          reject(new Error('Created blob is empty'));
          return;
        }
        
        // ADDITIONAL DEBUG: Check blob content
        if (blob.size < 1000) {
          console.warn('⚠️ Blob is very small (< 1KB), might be mostly empty');
        }
        
        try {
          // Create download link
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.style.display = 'none';
          
          // Add to DOM, click, and remove
          document.body.appendChild(link);
          console.log('Triggering download...');
          link.click();
          
          // Clean up after a short delay
          setTimeout(() => {
            try {
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
              console.log('Download triggered and cleaned up');
              resolve();
            } catch (cleanupError) {
              console.warn('Cleanup warning:', cleanupError);
              resolve(); // Still resolve since download likely worked
            }
          }, 100);
          
        } catch (downloadError) {
          console.error('Download process failed:', downloadError);
          reject(downloadError);
        }
        
      }, 'image/png', 0.95);
    } catch (canvasError) {
      console.error('Canvas.toBlob failed:', canvasError);
      reject(canvasError);
    }
  });
}

/**
 * Updated main export function that tries the safe SVG method first,
 * then falls back to enhanced html2canvas
 */
export async function exportViewportToPNG(
  containerElement: HTMLElement,
  options: ExportOptions = {},
  metadata?: ViewportMetadata
): Promise<void> {
  const exportOptions = validateExportOptions(options);
  const svgElements = Array.from(containerElement.querySelectorAll('.number-line-svg'));
  const hasMultipleSVGs = svgElements.length > 1;
  const isEquivalentContainer =
    containerElement.hasAttribute('data-equivalent-fractions-container') ||
    !!containerElement.querySelector('[data-equivalent-fractions-container]');
  const needsFullScrollCapture =
    isEquivalentContainer ||
    hasMultipleSVGs ||
    containerElement.scrollHeight > containerElement.clientHeight ||
    containerElement.scrollWidth > containerElement.clientWidth;

  const captureWithHtml2Canvas = async () => {
    if (!needsFullScrollCapture) {
      await exportWithHtml2CanvasEnhanced(containerElement, exportOptions, metadata);
      return;
    }

    const rect = containerElement.getBoundingClientRect();
    const expandedWidth = Math.max(rect.width, containerElement.scrollWidth);
    const expandedHeight = Math.max(rect.height, containerElement.scrollHeight);
    const originalStyles = {
      width: containerElement.style.width,
      height: containerElement.style.height,
      maxWidth: containerElement.style.maxWidth,
      maxHeight: containerElement.style.maxHeight,
      overflow: containerElement.style.overflow,
      overflowX: (containerElement.style as any).overflowX,
      overflowY: (containerElement.style as any).overflowY,
    };

    containerElement.style.width = `${expandedWidth}px`;
    containerElement.style.height = `${expandedHeight}px`;
    containerElement.style.maxWidth = `${expandedWidth}px`;
    containerElement.style.maxHeight = `${expandedHeight}px`;
    containerElement.style.overflow = 'visible';
    (containerElement.style as any).overflowX = 'visible';
    (containerElement.style as any).overflowY = 'visible';
    containerElement.setAttribute('data-export-expanded', 'true');

    try {
      await exportWithHtml2CanvasEnhanced(
        containerElement,
        exportOptions,
        metadata
      );
    } finally {
      containerElement.style.width = originalStyles.width;
      containerElement.style.height = originalStyles.height;
      containerElement.style.maxWidth = originalStyles.maxWidth;
      containerElement.style.maxHeight = originalStyles.maxHeight;
      containerElement.style.overflow = originalStyles.overflow;
      (containerElement.style as any).overflowX = originalStyles.overflowX;
      (containerElement.style as any).overflowY = originalStyles.overflowY;
      containerElement.removeAttribute('data-export-expanded');
    }
  };

  try {
    console.log('Starting enhanced PNG export...');

    // Dedicated path for equivalent fractions: capture the inner container directly
    if (isEquivalentContainer) {
      const eqContainer = (containerElement.hasAttribute('data-equivalent-fractions-container')
        ? containerElement
        : (containerElement.querySelector('[data-equivalent-fractions-container]') as HTMLElement | null));
      if (!eqContainer) {
        throw new Error('Equivalent fractions container not found for export');
      }

      const orig = {
        width: eqContainer.style.width,
        height: eqContainer.style.height,
        maxWidth: eqContainer.style.maxWidth,
        maxHeight: eqContainer.style.maxHeight,
        overflow: eqContainer.style.overflow,
        overflowX: (eqContainer.style as any).overflowX,
        overflowY: (eqContainer.style as any).overflowY,
        position: eqContainer.style.position,
      };

      // Expand to full scroll size and unclip
      const expandedWidth = Math.max(eqContainer.clientWidth, eqContainer.scrollWidth);
      const expandedHeight = Math.max(eqContainer.clientHeight, eqContainer.scrollHeight);
      eqContainer.style.width = `${expandedWidth}px`;
      eqContainer.style.height = `${expandedHeight}px`;
      eqContainer.style.maxWidth = `${expandedWidth}px`;
      eqContainer.style.maxHeight = `${expandedHeight}px`;
      eqContainer.style.overflow = 'visible';
      (eqContainer.style as any).overflowX = 'visible';
      (eqContainer.style as any).overflowY = 'visible';
      eqContainer.style.position = 'relative';

      try {
        const html2canvas = (await import('html2canvas')).default;
        // Use type assertion for html2canvas options since type defs are incomplete
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const canvas = await html2canvas(eqContainer, {
          background: getBackgroundColor(exportOptions.background) || '#ffffff',
          useCORS: true,
          allowTaint: true,
          scrollX: 0,
          scrollY: 0,
          width: expandedWidth,
          height: expandedHeight,
          windowWidth: expandedWidth,
          windowHeight: expandedHeight,
          logging: false,
          onclone: (clonedDoc: Document): void => {
            // Safely inject KaTeX styles for fraction rendering (only if head exists)
            try {
              const katexCss = extractKaTeXCSS();
              if (katexCss && clonedDoc.head) {
                const existingStyle = clonedDoc.querySelector('style[data-export-inline-katex="true"]');
                if (!existingStyle) {
                  const styleEl = clonedDoc.createElement('style');
                  styleEl.setAttribute('data-export-inline-katex', 'true');
                  styleEl.textContent = katexCss;
                  clonedDoc.head.appendChild(styleEl);
                }
              }
            } catch (e) {
              // Ignore KaTeX injection errors - fractions may render as plain text
              console.warn('Could not inject KaTeX styles for export:', e);
            }

            // AGGRESSIVE FIX: Reset all text-affecting styles to prevent flipping/mirroring
            const allElements = clonedDoc.querySelectorAll('*');
            allElements.forEach((el) => {
              if (el instanceof HTMLElement || el instanceof SVGElement) {
                const style = (el as HTMLElement).style;
                
                // Force LTR direction
                style.setProperty('direction', 'ltr', 'important');
                style.setProperty('writing-mode', 'horizontal-tb', 'important');
                
                // Remove transforms that flip
                if (style.transform && (style.transform.includes('scale') || style.transform.includes('rotate'))) {
                  style.setProperty('transform', 'none', 'important');
                }
                
                // Ensure standard font to avoid rendering glitches
                if (el.tagName.toLowerCase() === 'text' || el.tagName.toLowerCase() === 'div') {
                  style.setProperty('font-family', 'Arial, Helvetica, sans-serif', 'important');
                  style.setProperty('font-variant', 'normal', 'important');
                }
              }
            });

            // Fix SVG text elements specifically
            const svgTexts = clonedDoc.querySelectorAll('svg text');
            svgTexts.forEach((text) => {
              if (text instanceof SVGTextElement) {
                text.style.setProperty('direction', 'ltr', 'important');
                text.removeAttribute('transform'); // Remove SVG-level transforms
                text.setAttribute('text-anchor', 'middle'); // Ensure centering
                text.style.setProperty('dominant-baseline', 'middle', 'important');
              }
            });

            // Ensure equivalent fractions container maintains layout
            const clonedEqContainer = clonedDoc.querySelector('[data-equivalent-fractions-container]');
            if (clonedEqContainer instanceof HTMLElement) {
              clonedEqContainer.style.width = `${expandedWidth}px`;
              clonedEqContainer.style.height = `${expandedHeight}px`;
              clonedEqContainer.style.overflow = 'visible';
              clonedEqContainer.style.position = 'relative';
              clonedEqContainer.style.transform = 'none'; // Ensure no container flip
            }

            // Fix rail container
            const clonedRails = clonedDoc.querySelectorAll('[data-equivalent-fractions-rail]');
            clonedRails.forEach((rail) => {
              if (rail instanceof HTMLElement) {
                rail.style.overflow = 'visible';
                rail.style.height = 'auto';
                rail.style.maxHeight = 'none';
                rail.style.transform = 'none';
              }
            });

            // Fix sticky elements to be static
            const stickies = clonedDoc.querySelectorAll('.sticky');
            stickies.forEach((sticky) => {
              if (sticky instanceof HTMLElement) {
                sticky.style.position = 'relative';
                sticky.style.top = '0';
                sticky.style.left = '0';
                sticky.style.right = 'auto';
                sticky.style.transform = 'none';
              }
            });
          },
        } as any);

        await downloadCanvasReliably(canvas, exportOptions.filename || 'number-line-export.png');
        return;
      } finally {
        eqContainer.style.width = orig.width;
        eqContainer.style.height = orig.height;
        eqContainer.style.maxWidth = orig.maxWidth;
        eqContainer.style.maxHeight = orig.maxHeight;
        eqContainer.style.overflow = orig.overflow;
        (eqContainer.style as any).overflowX = orig.overflowX;
        (eqContainer.style as any).overflowY = orig.overflowY;
        eqContainer.style.position = orig.position;
      }
    }

    // Prefer html2canvas when multiple SVGs or equivalent fractions container is present
    // Also prefer html2canvas when we have foreignObjects (like KaTeX fractions) because
    // drawing them to canvas via SVG data URL often fails to render external fonts/styles properly.
    if (!needsFullScrollCapture && svgElements.length === 1) {
      const svgElement = svgElements[0];
      const hasContent = svgElement.children.length > 0;
      const hasForeignObject = !!svgElement.querySelector('foreignObject');
      
      console.log('SVG content check:', { hasContent, childCount: svgElement.children.length, hasForeignObject });

      if (hasContent && !hasForeignObject) {
        console.log('Attempting safe SVG-to-canvas export...');
        try {
          await exportSVGToCanvasSafe(svgElement as SVGElement, exportOptions, metadata);
          console.log('Safe SVG-to-canvas export completed successfully');
          return;
        } catch (svgError) {
          console.log('Safe SVG-to-canvas export failed, falling back to enhanced html2canvas:', {
            message: svgError instanceof Error ? svgError.message : 'Unknown error',
            stack: svgError instanceof Error ? svgError.stack : undefined
          });
        }
      }
    }
    
    // Fallback to enhanced html2canvas method
    console.log('Using enhanced html2canvas fallback method...');
    await captureWithHtml2Canvas();
    
  } catch (error) {
    console.error('All export methods failed:', error);
    throw new Error(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Get the background color for html2canvas
 */
function getBackgroundColor(background?: ExportBackground): string | null {
  switch (background) {
    case 'white':
      return '#ffffff';
    case 'black':
      return '#000000';
    case 'transparent':
    default:
      return null;
  }
}

/**
 * Embed metadata into the PNG file
 * Note: This is a simplified implementation - full PNG metadata requires specialized libraries
 */
async function embedMetadata(canvas: HTMLCanvasElement, metadata: ViewportMetadata): Promise<void> {
  // For now, we'll store metadata in a comment that could be retrieved
  // A full implementation would use a PNG metadata library
  const metadataComment = `Number Line Export - Scale: ${metadata.scale}, Domain: ${metadata.domain.start} to ${metadata.domain.end}, Type: ${metadata.numberType}, Exported: ${metadata.timestamp}`;
  
  // Store in canvas data attribute for potential future use
  canvas.setAttribute('data-export-metadata', JSON.stringify(metadata));
  
  console.log('Metadata embedded:', metadataComment);
}

/**
 * Download the canvas as a PNG file
 */
async function downloadPNG(canvas: HTMLCanvasElement, filename: string): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Failed to create blob from canvas'));
          return;
        }
        
        // Create download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up
        URL.revokeObjectURL(url);
        resolve();
      }, 'image/png', 0.95);
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Generate a descriptive filename for the export
 */
function generateFilename(metadata?: ViewportMetadata): string {
  const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
  
  if (metadata) {
    const domain = `${metadata.domain.start}-to-${metadata.domain.end}`;
    return `number-line-${domain}-${timestamp}.png`;
  }
  
  return `number-line-export-${timestamp}.png`;
}

/**
 * Validate export options
 */
export function validateExportOptions(options: Partial<ExportOptions>): ExportOptions {
  return {
    background: options.background || 'transparent',
    filename: options.filename,
    scale: Math.max(1, Math.min(4, options.scale || 2)), // Clamp between 1-4x
    quality: Math.max(0.1, Math.min(1, options.quality || 0.95)), // Clamp between 0.1-1
  };
}

/**
 * Check if export is supported in the current browser
 */
export function isExportSupported(): boolean {
  try {
    // Check for required APIs
    return !!(
      typeof HTMLCanvasElement.prototype.toBlob === 'function' &&
      typeof document.createElement === 'function' &&
      typeof URL.createObjectURL === 'function'
    );
  } catch {
    return false;
  }
}

/**
 * Quick debug function to check export readiness
 * Run this in the console before exporting to see potential issues
 */
export function debugExportReadiness(): void {
  console.log('=== EXPORT READINESS CHECK ===');
  
  // Check container
  const container = document.querySelector('.number-line-container');
  console.log('Container found:', !!container);
  
  if (container) {
    // Check SVG elements
    const mainSVG = container.querySelector('.number-line-svg');
    const annotationSVG = container.querySelector('.annotation-svg');
    
    console.log('Main SVG found:', !!mainSVG);
    console.log('Annotation SVG found:', !!annotationSVG);
    
    if (mainSVG) {
      console.log('Main SVG details:', {
        width: mainSVG.getAttribute('width'),
        height: mainSVG.getAttribute('height'),
        childCount: mainSVG.children.length,
        hasViewBox: !!mainSVG.getAttribute('viewBox')
      });
    }
    
    if (annotationSVG) {
      console.log('Annotation SVG details:', {
        width: annotationSVG.getAttribute('width'),
        height: annotationSVG.getAttribute('height'),
        childCount: annotationSVG.children.length,
        hasViewBox: !!annotationSVG.getAttribute('viewBox')
      });
    }
    
    // Check for external resources that might cause tainted canvas
    const allImages = container.querySelectorAll('image');
    const externalImages = Array.from(allImages).filter(img => {
      const href = img.getAttribute('href') || img.getAttribute('xlink:href');
      return href && (href.startsWith('http') || href.startsWith('//'));
    });
    
    console.log('External images that could cause tainted canvas:', externalImages.length);
    
    // Check for external fonts
    const styleElements = document.querySelectorAll('style');
    let hasExternalFonts = false;
    styleElements.forEach(style => {
      if (style.textContent && style.textContent.includes('@import')) {
        hasExternalFonts = true;
      }
    });
    
    console.log('Has external font imports:', hasExternalFonts);
    
    // Summary
    if (!externalImages.length && !hasExternalFonts) {
      console.log('✅ Export should work with safe SVG method');
    } else {
      console.log('⚠️ May need to fall back to html2canvas due to external resources');
    }
  }
  
  console.log('=== END READINESS CHECK ===');
}

/**
 * Helper function to test just the SVG export method
 */
export async function testSVGExportOnly(
  options: ExportOptions = { background: 'white' }
): Promise<void> {
  try {
    const svgElement = document.querySelector('.number-line-svg') as SVGElement;
    if (!svgElement) {
      throw new Error('No SVG element found for testing');
    }
    
    console.log('Testing safe SVG export method only...');
    await exportSVGToCanvasSafe(svgElement, options);
    console.log('SVG export test completed successfully');
    
  } catch (error) {
    console.error('SVG export test failed:', error);
    throw error;
  }
}

/**
 * Helper function to test just the html2canvas method
 */
export async function testHtml2CanvasOnly(
  options: ExportOptions = { background: 'white' }
): Promise<void> {
  try {
    const container = document.querySelector('.number-line-container') as HTMLElement;
    if (!container) {
      throw new Error('No container element found for testing');
    }
    
    console.log('Testing enhanced html2canvas method only...');
    await exportWithHtml2CanvasEnhanced(container, options);
    console.log('html2canvas export test completed successfully');
    
  } catch (error) {
    console.error('html2canvas export test failed:', error);
    throw error;
  }
}

/**
 * Debug function to diagnose positioning issues in export
 * Run this in the console to get detailed positioning information
 */
export function debugExportPositioning(): void {
  console.log('=== EXPORT POSITIONING DEBUG ===');
  
  // Find the main container
  const container = document.querySelector('.number-line-container') as HTMLElement;
  if (!container) {
    console.error('Container not found!');
    return;
  }
  
  // Get container positioning info
  const containerRect = container.getBoundingClientRect();
  console.log('Container positioning:', {
    boundingRect: {
      left: containerRect.left,
      top: containerRect.top,
      width: containerRect.width,
      height: containerRect.height,
      right: containerRect.right,
      bottom: containerRect.bottom
    },
    offset: {
      offsetLeft: container.offsetLeft,
      offsetTop: container.offsetTop,
      offsetWidth: container.offsetWidth,
      offsetHeight: container.offsetHeight
    },
    scroll: {
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop,
      scrollWidth: container.scrollWidth,
      scrollHeight: container.scrollHeight
    },
    computed: window.getComputedStyle(container),
    margin: {
      marginLeft: window.getComputedStyle(container).marginLeft,
      marginTop: window.getComputedStyle(container).marginTop,
      marginRight: window.getComputedStyle(container).marginRight,
      marginBottom: window.getComputedStyle(container).marginBottom
    }
  });
  
  // Check export area specifically
  const exportArea = container.querySelector('.number-line-export-area') || 
                    container.querySelector('.number-line-svg')?.parentElement ||
                    container;
  
  const exportRect = exportArea.getBoundingClientRect();
  console.log('Export area positioning:', {
    element: exportArea.tagName,
    classes: exportArea.className,
    boundingRect: {
      left: exportRect.left,
      top: exportRect.top,
      width: exportRect.width,
      height: exportRect.height,
      right: exportRect.right,
      bottom: exportRect.bottom
    },
    style: {
      position: (exportArea as HTMLElement).style.position,
      left: (exportArea as HTMLElement).style.left,
      top: (exportArea as HTMLElement).style.top,
      transform: (exportArea as HTMLElement).style.transform,
      margin: (exportArea as HTMLElement).style.margin,
      padding: (exportArea as HTMLElement).style.padding
    }
  });
  
  // Check SVG positioning
  const svg = container.querySelector('.number-line-svg');
  if (svg) {
    const svgRect = svg.getBoundingClientRect();
    console.log('Main SVG positioning:', {
      boundingRect: {
        left: svgRect.left,
        top: svgRect.top,
        width: svgRect.width,
        height: svgRect.height
      },
      attributes: {
        width: svg.getAttribute('width'),
        height: svg.getAttribute('height'),
        viewBox: svg.getAttribute('viewBox')
      },
      style: {
        position: (svg as any).style.position,
        left: (svg as any).style.left,
        top: (svg as any).style.top,
        transform: (svg as any).style.transform
      }
    });
  }
  
  // Check for any elements with negative margins or transforms
  const allElements = container.querySelectorAll('*');
  const problemElements: any[] = [];
  
  allElements.forEach((el, index) => {
    const style = window.getComputedStyle(el);
    const hasNegativeMargin = style.marginLeft.startsWith('-') || style.marginTop.startsWith('-');
    const hasTransform = style.transform !== 'none';
    const hasNegativePosition = style.left.startsWith('-') || style.top.startsWith('-');
    
    if (hasNegativeMargin || hasTransform || hasNegativePosition) {
      problemElements.push({
        element: el.tagName,
        classes: el.className,
        index: index,
        issues: {
          negativeMargin: hasNegativeMargin,
          transform: hasTransform ? style.transform : false,
          negativePosition: hasNegativePosition,
          marginLeft: style.marginLeft,
          marginTop: style.marginTop,
          left: style.left,
          top: style.top
        }
      });
    }
  });
  
  console.log('Elements that might cause positioning issues:', problemElements);
  
  // Check viewport and scroll
  console.log('Viewport and scroll info:', {
    window: {
      innerWidth: window.innerWidth,
      innerHeight: window.innerHeight,
      pageXOffset: window.pageXOffset,
      pageYOffset: window.pageYOffset
    },
    document: {
      scrollLeft: document.documentElement.scrollLeft,
      scrollTop: document.documentElement.scrollTop,
      clientWidth: document.documentElement.clientWidth,
      clientHeight: document.documentElement.clientHeight
    }
  });
  
  // Visual test - add a temporary border to see the exact capture area
  const testBorder = document.createElement('div');
  testBorder.style.position = 'absolute';
  testBorder.style.left = exportRect.left + 'px';
  testBorder.style.top = exportRect.top + 'px';
  testBorder.style.width = exportRect.width + 'px';
  testBorder.style.height = exportRect.height + 'px';
  testBorder.style.border = '3px solid red';
  testBorder.style.pointerEvents = 'none';
  testBorder.style.zIndex = '10000';
  testBorder.id = 'export-debug-border';
  
  // Remove any existing debug border
  const existing = document.getElementById('export-debug-border');
  if (existing) existing.remove();
  
  document.body.appendChild(testBorder);
  
  console.log('Added red border around export area. Remove it by running: document.getElementById("export-debug-border").remove()');
  
  console.log('=== END POSITIONING DEBUG ===');
}

/**
 * Quick fix function to apply common positioning fixes
 * Run this in the console to automatically fix common export offset issues
 */
export function quickFixExportOffset(): void {
  console.log('Applying quick fixes for export offset...');
  
  const container = document.querySelector('.number-line-container') as HTMLElement;
  if (!container) return;
  
  // Remove negative margins that might cause cutoff
  const allElements = container.querySelectorAll('*');
  allElements.forEach(el => {
    if (el instanceof HTMLElement) {
      const style = window.getComputedStyle(el);
      if (style.marginLeft.startsWith('-')) {
        el.style.marginLeft = '0';
        console.log('Fixed negative marginLeft on', el.tagName, el.className);
      }
      if (style.marginTop.startsWith('-')) {
        el.style.marginTop = '0';
        console.log('Fixed negative marginTop on', el.tagName, el.className);
      }
    }
  });
  
  // Ensure container has no offset
  container.style.position = 'relative';
  container.style.left = '0';
  container.style.top = '0';
  
  console.log('Quick fixes applied. Try exporting again.');
}

// Additional debug function to test positioning before export
export function debugVerticalPositioning(): void {
  console.log('=== VERTICAL POSITIONING DEBUG ===');
  
  const container = document.querySelector('.number-line-container') as HTMLElement;
  if (!container) {
    console.error('Container not found!');
    return;
  }
  
  const rect = container.getBoundingClientRect();
  console.log('Container positioning analysis:', {
    boundingClientRect: {
      top: rect.top,
      bottom: rect.bottom,
      height: rect.height,
      y: rect.y
    },
    offset: {
      offsetTop: container.offsetTop,
      offsetHeight: container.offsetHeight
    },
    scroll: {
      scrollTop: container.scrollTop,
      pageYOffset: window.pageYOffset,
      scrollY: window.scrollY
    },
    computed: {
      marginTop: window.getComputedStyle(container).marginTop,
      paddingTop: window.getComputedStyle(container).paddingTop,
      position: window.getComputedStyle(container).position,
      top: window.getComputedStyle(container).top
    }
  });
  
  // Check if there are any elements causing vertical offset
  const exportArea = container.querySelector('.number-line-export-area');
  if (exportArea) {
    const areaRect = exportArea.getBoundingClientRect();
    console.log('Export area vertical positioning:', {
      top: areaRect.top,
      bottom: areaRect.bottom,
      height: areaRect.height,
      offsetFromContainer: areaRect.top - rect.top
    });
  }
  
  console.log('=== END VERTICAL DEBUG ===');
}

// Enhanced debug function to run before export
export function debugExportIssues(): void {
  console.log('=== COMPREHENSIVE EXPORT DEBUG ===');
  
  const container = document.querySelector('.number-line-container') as HTMLElement;
  if (!container) {
    console.error('❌ Container not found!');
    return;
  }
  
  const exportArea = container.querySelector('.number-line-export-area') || 
                    container.querySelector('.number-line-svg')?.parentElement ||
                    container;
  
  const containerRect = container.getBoundingClientRect();
  const exportRect = exportArea.getBoundingClientRect();
  
  console.log('📐 Container analysis:', {
    element: container.tagName + '.' + container.className,
    boundingRect: {
      left: containerRect.left,
      top: containerRect.top,
      width: containerRect.width,
      height: containerRect.height
    },
    computedStyle: {
      position: window.getComputedStyle(container).position,
      marginLeft: window.getComputedStyle(container).marginLeft,
      marginTop: window.getComputedStyle(container).marginTop,
      paddingLeft: window.getComputedStyle(container).paddingLeft,
      paddingTop: window.getComputedStyle(container).paddingTop,
      transform: window.getComputedStyle(container).transform
    }
  });
  
  console.log('📐 Export area analysis:', {
    element: exportArea.tagName + '.' + exportArea.className,
    boundingRect: {
      left: exportRect.left,
      top: exportRect.top,
      width: exportRect.width,
      height: exportRect.height
    },
    offsetFromContainer: {
      x: exportRect.left - containerRect.left,
      y: exportRect.top - containerRect.top
    },
    computedStyle: {
      position: window.getComputedStyle(exportArea as HTMLElement).position,
      marginLeft: window.getComputedStyle(exportArea as HTMLElement).marginLeft,
      marginTop: window.getComputedStyle(exportArea as HTMLElement).marginTop,
      transform: window.getComputedStyle(exportArea as HTMLElement).transform
    }
  });
  
  // Check for problematic elements
  const elementsWithNegativeMargins = container.querySelectorAll('*');
  const problematicElements = Array.from(elementsWithNegativeMargins).filter(el => {
    if (el instanceof HTMLElement) {
      const style = window.getComputedStyle(el);
      return style.marginLeft.startsWith('-') || 
             style.marginTop.startsWith('-') ||
             (style.transform && style.transform !== 'none');
    }
    return false;
  });
  
  if (problematicElements.length > 0) {
    console.log('⚠️ Found elements with potential positioning issues:', 
      problematicElements.map(el => ({
        tag: el.tagName,
        class: (el as HTMLElement).className,
        marginLeft: window.getComputedStyle(el as HTMLElement).marginLeft,
        marginTop: window.getComputedStyle(el as HTMLElement).marginTop,
        transform: window.getComputedStyle(el as HTMLElement).transform
      }))
    );
  } else {
    console.log('✅ No obvious positioning issues found');
  }
  
  // Check SVG elements specifically
  const svgElements = container.querySelectorAll('svg');
  console.log('📊 SVG elements found:', svgElements.length);
  svgElements.forEach((svg, index) => {
    const svgRect = svg.getBoundingClientRect();
    console.log(`SVG ${index + 1}:`, {
      width: svg.getAttribute('width'),
      height: svg.getAttribute('height'),
      viewBox: svg.getAttribute('viewBox'),
      boundingRect: {
        left: svgRect.left,
        top: svgRect.top,
        width: svgRect.width,
        height: svgRect.height
      },
      offsetFromContainer: {
        x: svgRect.left - containerRect.left,
        y: svgRect.top - containerRect.top
      }
    });
  });
  
  console.log('=== END COMPREHENSIVE DEBUG ===');
}

// Quick fix function you can run in console before export
export function applyExportFixes(): void {
  console.log('🔧 Applying export positioning fixes...');
  
  const container = document.querySelector('.number-line-container') as HTMLElement;
  if (!container) {
    console.error('❌ Container not found!');
    return;
  }
  
  // Fix container positioning
  container.style.position = 'relative';
  container.style.left = '0';
  container.style.top = '0';
  
  // Fix all child elements with positioning issues
  const allElements = container.querySelectorAll('*');
  allElements.forEach(el => {
    if (el instanceof HTMLElement) {
      const style = window.getComputedStyle(el);
      
      // Fix negative margins
      if (style.marginLeft.startsWith('-')) {
        el.style.marginLeft = '0';
        console.log('✅ Fixed negative marginLeft on', el.tagName, el.className);
      }
      if (style.marginTop.startsWith('-')) {
        el.style.marginTop = '0';
        console.log('✅ Fixed negative marginTop on', el.tagName, el.className);
      }
    }
  });
  
  console.log('✅ Export fixes applied! Try exporting again.');
}

// Quick debug function to run in console
export function checkExportElements(): void {
  console.log('=== CHECKING EXPORT ELEMENTS ===');
  
  const container = document.querySelector('.number-line-container') as HTMLElement;
  if (!container) {
    console.error('❌ No container found');
    return;
  }
  
  console.log('✅ Container found:', {
    tagName: container.tagName,
    className: container.className,
    offsetWidth: container.offsetWidth,
    offsetHeight: container.offsetHeight,
    childElementCount: container.childElementCount
  });
  
  // Check for SVG
  const svg = container.querySelector('.number-line-svg');
  if (svg) {
    console.log('✅ Main SVG found:', {
      width: svg.getAttribute('width'),
      height: svg.getAttribute('height'),
      childElementCount: svg.childElementCount,
      hasContent: svg.children.length > 0
    });
  } else {
    console.log('❌ No main SVG found');
  }
  
  // Check for annotations
  const annotations = container.querySelector('.annotation-canvas') || 
                     container.querySelector('[class*="annotation"]');
  if (annotations) {
    console.log('✅ Annotation layer found:', {
      tagName: annotations.tagName,
      className: annotations.className
    });
  } else {
    console.log('ℹ️ No annotation layer found (this is okay if you have no annotations)');
  }
  
  console.log('=== END CHECK ===');
}

// Add this function to your src/lib/export.ts file - this fixes both CORS and complex element issues

// Fixed export function with proper alignment for SVG and annotation layers

export async function exportToPNGFixed(
  svgElement: SVGSVGElement,
  filename: string = 'number-line-export.png',
  scale: number = 2
): Promise<void> {
  console.log('🚀 Starting CORS-safe PNG export...');
  
  try {
    // Try the pre-render approach which handles foreignObject/KaTeX properly
    await exportWithForeignObjectPrerender(svgElement, filename, scale);
  } catch (error) {
    console.warn('⚠️ Pre-render method failed, trying coordinate matching...', error);
    try {
      await exportWithCoordinateMatching(svgElement, filename, scale);
    } catch (fallbackError) {
      console.error('❌ All export methods failed:', fallbackError);
      throw fallbackError;
    }
  }
}

/**
 * Pre-render foreignObject elements (like KaTeX fractions) to images before export.
 * This ensures html2canvas can capture them correctly since it has poor foreignObject support.
 */
async function exportWithForeignObjectPrerender(
  svgElement: SVGSVGElement,
  filename: string,
  scale: number
): Promise<void> {
  console.log('🎯 Using foreignObject pre-render method...');
  
  const container = svgElement.closest('[data-numberline-container]') ||
                   svgElement.closest('.number-line-container') ||
                   svgElement.closest('[data-testid="number-line-canvas"]') ||
                   svgElement.parentElement;
  
  if (!container) {
    throw new Error('Could not find number line container');
  }

  const containerRect = container.getBoundingClientRect();
  console.log('📏 Container dimensions:', { 
    width: containerRect.width, 
    height: containerRect.height
  });

  // Find all foreignObject elements in the container (these contain KaTeX/fraction labels)
  const foreignObjects = Array.from(container.querySelectorAll('foreignObject'));
  console.log(`📝 Found ${foreignObjects.length} foreignObject elements to pre-render`);

  // Pre-render each foreignObject to a data URL
  const html2canvas = (await import('html2canvas')).default;
  const prerenderedImages: Map<string, string> = new Map();
  
  for (let i = 0; i < foreignObjects.length; i++) {
    const fo = foreignObjects[i] as SVGForeignObjectElement;
    const foRect = fo.getBoundingClientRect();
    
    // Skip if element is too small or not visible
    if (foRect.width < 1 || foRect.height < 1) {
      continue;
    }

    // Get the inner content
    const innerDiv = fo.querySelector('div');
    if (!innerDiv) continue;

    // Generate a unique ID for this foreignObject
    const foId = `fo-${i}-${Date.now()}`;
    fo.setAttribute('data-fo-id', foId);

    try {
      // Create an off-screen container for rendering
      const tempContainer = document.createElement('div');
      tempContainer.style.cssText = `
        position: fixed;
        left: -9999px;
        top: -9999px;
        width: ${foRect.width * 2}px;
        height: ${foRect.height * 2}px;
        overflow: visible;
        background: transparent;
        pointer-events: none;
        z-index: -9999;
      `;
      
      // Clone the content with all styles
      const contentClone = innerDiv.cloneNode(true) as HTMLElement;
      contentClone.style.cssText = `
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        font-size: ${window.getComputedStyle(innerDiv).fontSize || '14px'};
        color: ${window.getComputedStyle(innerDiv).color || '#000'};
      `;
      
      // Inject KaTeX styles into the temp container
      const katexCss = extractKaTeXCSS();
      if (katexCss) {
        const styleEl = document.createElement('style');
        styleEl.textContent = katexCss;
        tempContainer.appendChild(styleEl);
      }
      
      tempContainer.appendChild(contentClone);
      document.body.appendChild(tempContainer);

      // Wait for fonts/styles to apply
      await new Promise(resolve => setTimeout(resolve, 50));

      // Render to canvas with higher scale for quality
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const foCanvas = await html2canvas(tempContainer, {
        background: undefined, // transparent
        scale: scale * 2, // Higher resolution for crisp text
        logging: false,
        useCORS: true,
        allowTaint: true,
      } as any);

      // Convert to data URL
      const dataUrl = foCanvas.toDataURL('image/png');
      prerenderedImages.set(foId, dataUrl);
      
      // Clean up temp container
      document.body.removeChild(tempContainer);
      
      console.log(`✅ Pre-rendered foreignObject ${i + 1}/${foreignObjects.length}`);
    } catch (err) {
      console.warn(`⚠️ Failed to pre-render foreignObject ${i + 1}:`, err);
    }
  }

  console.log(`📦 Pre-rendered ${prerenderedImages.size} foreignObject elements`);

  // Now do the main export with html2canvas, replacing foreignObjects in the clone
  console.log('🎨 Capturing container with pre-rendered labels...');
  
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas context');

  canvas.width = containerRect.width * scale;
  canvas.height = containerRect.height * scale;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.scale(scale, scale);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const containerCanvas = await html2canvas(container as HTMLElement, {
    background: undefined, // transparent
    scale: 1,
    useCORS: true,
    allowTaint: true,
    width: containerRect.width,
    height: containerRect.height,
    windowWidth: containerRect.width,
    windowHeight: containerRect.height,
    logging: false,
    onclone: (clonedDoc: Document): void => {
      console.log('🔧 Replacing foreignObjects in clone with pre-rendered images...');
      
      // Inject KaTeX styles
      injectKaTeXStyles(clonedDoc);
      
      // Replace each foreignObject with the pre-rendered image
      const clonedFOs = clonedDoc.querySelectorAll('foreignObject[data-fo-id]');
      clonedFOs.forEach((clonedFo) => {
        const foId = clonedFo.getAttribute('data-fo-id');
        if (!foId) return;
        
        const dataUrl = prerenderedImages.get(foId);
        if (!dataUrl) return;
        
        const width = clonedFo.getAttribute('width') || '60';
        const height = clonedFo.getAttribute('height') || '28';
        const x = clonedFo.getAttribute('x') || '0';
        const y = clonedFo.getAttribute('y') || '0';
        
        // Create an SVG image element with the pre-rendered content
        const img = clonedDoc.createElementNS('http://www.w3.org/2000/svg', 'image');
        img.setAttribute('x', x);
        img.setAttribute('y', y);
        img.setAttribute('width', width);
        img.setAttribute('height', height);
        img.setAttribute('href', dataUrl);
        img.setAttribute('preserveAspectRatio', 'xMidYMid meet');
        
        // Replace the foreignObject with the image
        clonedFo.parentNode?.replaceChild(img, clonedFo);
      });
      
      console.log(`✅ Replaced ${clonedFOs.length} foreignObjects with pre-rendered images`);
    },
  } as any);

  ctx.drawImage(containerCanvas, 0, 0, containerRect.width, containerRect.height);

  console.log('📸 Capture completed, downloading...');
  
  // Clean up: remove data-fo-id attributes from original DOM
  foreignObjects.forEach(fo => fo.removeAttribute('data-fo-id'));
  
  // Download
  await new Promise<void>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Failed to create blob'));
        return;
      }
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log('✅ Pre-render export completed successfully');
      resolve();
    }, 'image/png', 0.95);
  });
}

// Coordinate-matched composition - ensures perfect alignment by using container's actual coordinates
async function exportWithCoordinateMatching(
  svgElement: SVGSVGElement,
  filename: string,
  scale: number
): Promise<void> {
  console.log('🎯 Using coordinate-matched composition method...');
  
  // IMPORTANT: prefer the outer container that also holds the annotation layer.
  // The inner `.number-line-container` only wraps the SVG and excludes annotations,
  // which caused missing overlays in PNG exports.
  const container = svgElement.closest('[data-numberline-container]') ||
                   svgElement.closest('.number-line-container') ||
                   svgElement.closest('[data-testid="number-line-canvas"]') ||
                   svgElement.parentElement;
  
  if (!container) {
    throw new Error('Could not find number line container');
  }

  const containerRect = container.getBoundingClientRect();
  console.log('📏 Container dimensions:', { 
    width: containerRect.width, 
    height: containerRect.height,
    x: containerRect.x,
    y: containerRect.y 
  });
  
  // Create main canvas with exact container dimensions
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    throw new Error('Could not get canvas context');
  }
  
  canvas.width = containerRect.width * scale;
  canvas.height = containerRect.height * scale;
  
  // Set white background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  // Important: Scale the context for all subsequent operations
  ctx.scale(scale, scale);
  
  console.log('🖼️ Canvas created:', { 
    width: canvas.width, 
    height: canvas.height,
    scaledWidth: containerRect.width,
    scaledHeight: containerRect.height
  });
  
  // Step 1: Use html2canvas to capture the entire container (this gets annotations right)
  console.log('🎨 Step 1: Capturing entire container with html2canvas...');
  const html2canvas = (await import('html2canvas')).default;
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const containerCanvas = await html2canvas(container as HTMLElement, {
    background: undefined, // Transparent to overlay properly
    scale: 1, // Use scale 1 since we're scaling the context
    useCORS: true,
    allowTaint: true,
    width: containerRect.width,
    height: containerRect.height,
    windowWidth: containerRect.width,
    windowHeight: containerRect.height,
    onclone: (clonedDoc: Document): void => {
      console.log('🔧 Fixing cloned elements for coordinate matching...');
      
      // Ensure KaTeX styles are present in the clone
      injectKaTeXStyles(clonedDoc);
      
      // Find the cloned container
      const clonedContainer = clonedDoc.querySelector('.number-line-container') || clonedDoc.querySelector('[data-numberline-container]');
      if (clonedContainer && clonedContainer instanceof HTMLElement) {
        // Ensure container maintains exact dimensions
        clonedContainer.style.width = containerRect.width + 'px';
        clonedContainer.style.height = containerRect.height + 'px';
        clonedContainer.style.position = 'relative';
        clonedContainer.style.overflow = 'visible';
      }
      
      // Fix all SVG elements to maintain their exact positions and sizes
      const clonedSvgs = clonedDoc.querySelectorAll('svg');
      clonedSvgs.forEach((svg: SVGSVGElement, index: number) => {
        const originalSvg = container.querySelectorAll('svg')[index];
        if (originalSvg) {
          const originalRect = originalSvg.getBoundingClientRect();
          const relativeX = originalRect.x - containerRect.x;
          const relativeY = originalRect.y - containerRect.y;
          
          // Set exact positioning and dimensions
          svg.style.position = 'absolute';
          svg.style.left = relativeX + 'px';
          svg.style.top = relativeY + 'px';
          svg.style.width = originalRect.width + 'px';
          svg.style.height = originalRect.height + 'px';
          svg.setAttribute('width', originalRect.width.toString());
          svg.setAttribute('height', originalRect.height.toString());
          
          // Preserve viewBox if it exists
          const viewBox = originalSvg.getAttribute('viewBox');
          if (viewBox) {
            svg.setAttribute('viewBox', viewBox);
          }
          
          console.log(`✅ Fixed SVG ${index + 1} positioning:`, {
            left: relativeX,
            top: relativeY,
            width: originalRect.width,
            height: originalRect.height
          });
        }
      });
      
      // Fix annotation elements to maintain exact positioning
      const clonedAnnotations = clonedDoc.querySelectorAll('[class*="annotation"]');
      clonedAnnotations.forEach((annotation: Element, index: number) => {
        if (annotation instanceof HTMLElement) {
          annotation.style.display = 'block';
          annotation.style.visibility = 'visible';
          annotation.style.opacity = '1';
          annotation.style.position = 'absolute';
          annotation.style.top = '0';
          annotation.style.left = '0';
          annotation.style.width = containerRect.width + 'px';
          annotation.style.height = containerRect.height + 'px';
        }
        
        console.log(`✅ Fixed annotation ${index + 1} positioning`);
      });
      
      console.log('✅ Coordinate matching fixes completed');
    }
  } as any);
  
  console.log('📸 html2canvas capture completed:', {
    capturedWidth: containerCanvas.width,
    capturedHeight: containerCanvas.height
  });
  
  // Draw the captured container onto our main canvas
  ctx.drawImage(containerCanvas, 0, 0, containerRect.width, containerRect.height);
  
  // Analyze the result
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const pixels = imageData.data;
  let whitePixels = 0;
  let totalPixels = pixels.length / 4;
  
  for (let i = 0; i < pixels.length; i += 4) {
    const r = pixels[i];
    const g = pixels[i + 1];
    const b = pixels[i + 2];
    
    if (r > 250 && g > 250 && b > 250) {
      whitePixels++;
    }
  }
  
  const whitePercentage = (whitePixels / totalPixels) * 100;
  console.log('🎨 Coordinate-matched composition analysis:', {
    whitePercentage: whitePercentage.toFixed(2) + '%',
    hasContent: whitePercentage < 90
  });
  

  
  // Download
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Failed to create blob from coordinate-matched canvas'));
        return;
      }
      
      console.log('✅ Coordinate-matched blob created:', { size: blob.size });
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      
      console.log('✅ Coordinate-matched export completed successfully');
      resolve();
    }, 'image/png', 0.95);
  });
}



async function exportWithImprovedHtml2Canvas(
  svgElement: SVGSVGElement,
  filename: string,
  scale: number
): Promise<void> {
  console.log('🎯 Using improved html2canvas method...');
  
  // Import html2canvas dynamically
  const html2canvas = (await import('html2canvas')).default;
  
  // Find the container that holds everything
  const container = svgElement.closest('.number-line-container') || 
                   svgElement.closest('[data-numberline-container]') ||
                   svgElement.closest('[data-testid="number-line-canvas"]') ||
                   svgElement.parentElement;
  
  if (!container) {
    throw new Error('Could not find number line container');
  }

  console.log('📦 Container found:', container.className);
  
  // Wait for any animations/transitions to complete
  await new Promise(resolve => setTimeout(resolve, 300));
  
  // Get container bounds
  const rect = container.getBoundingClientRect();
  console.log('📏 Container bounds:', {
    width: rect.width,
    height: rect.height,
    x: rect.x,
    y: rect.y
  });

  // Pre-capture analysis
  console.log('=== PRE-CAPTURE ANALYSIS ===');
  const elements = container.querySelectorAll('*');
  const svgElements = container.querySelectorAll('svg');
  const annotations = container.querySelectorAll('[class*="annotation"]');
  const secondaryLines = container.querySelectorAll('[class*="secondary"]');
  const constants = container.querySelectorAll('[class*="constant"]');
  
  console.log('📊 Element counts:', {
    total: elements.length,
    svgs: svgElements.length,
    annotations: annotations.length,
    secondaryLines: secondaryLines.length,
    constants: constants.length
  });

  // Capture with optimized settings for complex content
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const canvas = await html2canvas(container as HTMLElement, {
    background: getBackgroundColor('white') || '#ffffff',
    useCORS: true,
    allowTaint: true,  // Allow tainted canvas for better compatibility
    onclone: (clonedDoc: Document): void => {
      console.log('🔧 Applying clone fixes...');
      
      // Keep KaTeX styling for fraction labels
      injectKaTeXStyles(clonedDoc);
      
      // Fix SVG elements in cloned document
      const clonedSvgs = clonedDoc.querySelectorAll('svg');
      clonedSvgs.forEach((svg, index) => {
        // Ensure SVG has proper attributes
        if (!svg.getAttribute('width')) svg.setAttribute('width', '100%');
        if (!svg.getAttribute('height')) svg.setAttribute('height', '100%');
        (svg as unknown as HTMLElement).style.display = 'block';
        (svg as unknown as HTMLElement).style.overflow = 'visible';
        console.log(`✅ Fixed cloned SVG ${index + 1}`);
      });
      
      // Fix annotation elements
      const clonedAnnotations = clonedDoc.querySelectorAll('[class*="annotation"]');
      clonedAnnotations.forEach((annotation, index) => {
        (annotation as HTMLElement).style.display = 'block';
        (annotation as HTMLElement).style.visibility = 'visible';
        (annotation as HTMLElement).style.opacity = '1';
        console.log(`✅ Fixed cloned annotation ${index + 1}`);
      });
      
      // Fix secondary line elements
      const clonedSecondaryLines = clonedDoc.querySelectorAll('[class*="secondary"]');
      clonedSecondaryLines.forEach((line, index) => {
        (line as HTMLElement).style.display = 'block';
        (line as HTMLElement).style.visibility = 'visible';
        (line as HTMLElement).style.opacity = '1';
        console.log(`✅ Fixed cloned secondary line ${index + 1}`);
      });
      
      // Fix constant elements
      const clonedConstants = clonedDoc.querySelectorAll('[class*="constant"]');
      clonedConstants.forEach((constant, index) => {
        (constant as HTMLElement).style.display = 'block';
        (constant as HTMLElement).style.visibility = 'visible';
        (constant as HTMLElement).style.opacity = '1';
        console.log(`✅ Fixed cloned constant ${index + 1}`);
      });
      
      console.log('✅ Clone fixes completed');
    },
    width: rect.width,
    height: rect.height,
    scrollX: 0,
    scrollY: 0,
    windowWidth: rect.width,
    windowHeight: rect.height
  } as any);

  console.log('📸 Capture completed:', {
    canvasWidth: canvas.width,
    canvasHeight: canvas.height
  });

  // Analyze captured content
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    
    let whitePixels = 0;
    let totalPixels = pixels.length / 4;
    
    for (let i = 0; i < pixels.length; i += 4) {
      const r = pixels[i];
      const g = pixels[i + 1];
      const b = pixels[i + 2];
      
      // Consider a pixel "white" if it's very close to white
      if (r > 250 && g > 250 && b > 250) {
        whitePixels++;
      }
    }
    
    const whitePercentage = (whitePixels / totalPixels) * 100;
    console.log('🎨 Content analysis:', {
      whitePixels,
      totalPixels,
      whitePercentage: whitePercentage.toFixed(2) + '%',
      hasContent: whitePercentage < 90
    });
    

  }

  // Embed metadata
  const metadata = `Number Line Export - Scale: ${scale}, Exported: ${new Date().toISOString()}`;
  console.log('📝 Metadata embedded:', metadata);

  // Download the canvas
  console.log('💾 Starting download...');
  
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Failed to create blob from canvas'));
        return;
      }
      
      console.log('✅ Blob created successfully:', {
        size: blob.size,
        type: blob.type
      });
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      
      console.log('✅ Download completed successfully');
      resolve();
    }, 'image/png', 0.95);
  });
}

// Alternative method using direct DOM-to-canvas conversion (fallback)
async function exportWithDOMToCanvas(
  svgElement: SVGSVGElement,
  filename: string,
  scale: number
): Promise<void> {
  console.log('🎯 Using DOM-to-canvas conversion...');
  
  const container = svgElement.closest('.number-line-container') || 
                   svgElement.parentElement;
  
  if (!container) {
    throw new Error('Could not find container');
  }
  
  const rect = container.getBoundingClientRect();
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    throw new Error('Could not get canvas context');
  }
  
  canvas.width = rect.width * scale;
  canvas.height = rect.height * scale;
  
  // Set white background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  // Scale the context
  ctx.scale(scale, scale);
  
  // Convert each SVG to canvas
  const svgs = container.querySelectorAll('svg');
  for (const svg of svgs) {
    await drawSVGToCanvas(svg as SVGSVGElement, ctx, scale);
  }
  
  // Download
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Failed to create blob'));
        return;
      }
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      resolve();
    }, 'image/png');
  });
}

async function drawSVGToCanvas(
  svg: SVGSVGElement, 
  ctx: CanvasRenderingContext2D, 
  scale: number
): Promise<void> {
  return new Promise((resolve, reject) => {
    const svgRect = svg.getBoundingClientRect();
    const svgData = new XMLSerializer().serializeToString(svg);
    
    // Clean up the SVG data to avoid CORS issues
    const cleanSvgData = svgData
      .replace(/xlink:href="[^"]*"/g, '') // Remove external links
      .replace(/href="[^"]*"/g, '') // Remove external links
      .replace(/url\([^)]*\)/g, 'none'); // Remove URL references
    
    const blob = new Blob([cleanSvgData], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, svgRect.x, svgRect.y, svgRect.width, svgRect.height);
      URL.revokeObjectURL(url);
      resolve();
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load SVG as image'));
    };
    
    img.src = url;
  });
}